import { drawCards, SPREADS, type Draw } from "@hexling/core";
import { Button, EmptyState, SelectField, Spinner, Switch } from "@hexling/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { PageTitle } from "../../components/Layout";
import { useContent, useServices } from "../../data/AppData";

export function TarotPage({ rng = Math.random }: { rng?: () => number }) {
  const content = useContent();
  const { user } = useServices();
  const navigate = useNavigate();
  const [spreadSlug, setSpreadSlug] = useState("three");
  const [reversals, setReversals] = useState(true);
  const [draws, setDraws] = useState<Draw[] | null>(null);

  if (content.isPending) return <Spinner />;
  const deck = content.data?.list("tarot_card") ?? [];
  if (!deck.length)
    return (
      <EmptyState glyph="🃏" title="The deck hasn't downloaded yet">
        Connect once to save all 78 cards on this device.
      </EmptyState>
    );

  const spread = SPREADS.find((s) => s.slug === spreadSlug)!;
  const draw = () => setDraws(drawCards(deck.length, spread.positions.length, rng, reversals));

  const saveReading = async () => {
    const lines = draws!.map(
      (d, i) =>
        `**${spread.positions[i]}:** ${deck[d.index]!.name}${d.reversed ? " (reversed)" : ""}`,
    );
    const note = await user.saveNote({
      title: `${spread.name} reading`,
      body: lines.join("\n\n"),
      tags: ["tarot"],
      refs: draws!.map((d) => ({ category: "tarot_card" as const, slug: deck[d.index]!.slug })),
      occurredOn: new Date().toISOString().slice(0, 10),
    });
    await navigate({ to: "/grimoire/notes/$id", params: { id: note.id } });
  };

  return (
    <>
      <PageTitle
        title="Tarot"
        subtitle="Shuffle, breathe, ask your question, then draw."
        back={{ to: "/library", label: "Library" }}
      />
      <div className="tarot-controls">
        <SelectField
          label="Spread"
          value={spreadSlug}
          onChange={(e) => (setSpreadSlug(e.target.value), setDraws(null))}
          options={SPREADS.map((s) => ({
            value: s.slug,
            label: `${s.name} (${s.positions.length})`,
          }))}
        />
        <Switch label="Include reversed cards" checked={reversals} onChange={setReversals} />
        <Button onClick={draw}>{draws ? "Draw again" : "Draw cards"}</Button>
      </div>

      {draws && (
        <section aria-labelledby="reading" className="reading">
          <h2 id="reading">{spread.name}</h2>
          <ol className="reading__cards">
            {draws.map((d, i) => {
              const card = deck[d.index]!;
              return (
                <li key={card.slug} className="reading__card">
                  <span className="reading__position">{spread.positions[i]}</span>
                  <Link
                    to="/library/$category/$slug"
                    params={{ category: "tarot_card", slug: card.slug }}
                    className={d.reversed ? "tarot-name tarot-name--reversed" : "tarot-name"}
                  >
                    {card.name}
                  </Link>
                  {d.reversed && <span className="hx-muted"> reversed</span>}
                  <p>
                    {String(
                      (d.reversed ? card.data.meaning_reversed : card.data.meaning_upright) ?? "",
                    )}
                  </p>
                </li>
              );
            })}
          </ol>
          <Button variant="secondary" onClick={saveReading}>
            Save reading to my grimoire
          </Button>
        </section>
      )}
    </>
  );
}
